/**
 * ReviewCaseFile — the ONE surface for a Proctor review case (YAG-440).
 *
 * The Work page's case file and the PR Reviews modal render THIS component,
 * so acting and understanding stop living on different screens: the held
 * draft's keep/edit/remove + Confirm (the case file's powers) and the
 * verdict, specialists, and re-run (the modal's powers) are one body.
 *
 * Anatomy, in order:
 *   header       team · worker · re-run · status pill · spine · title with the
 *                grey #N GitHub suffix · mono byline
 *   THE ASK      always open: the waiting line, THE REVIEW card (verdict chip
 *                first — their call, before your decisions), the held findings,
 *                the gate (the SAME GateBlock the generic case file mounts),
 *                the live run.
 *   SPECIALISTS  collapsed: the pipeline as accordion rows (pipeline.ts).
 *   MORE DETAILS collapsed: facts · receipts · corrections · runs · passes.
 *   HISTORY      collapsed: the raw audit rows.
 *
 * Data rules (the two-table seam): identity is the work item id. The PR-side
 * read (`useReviewCasePr`) resolves through `code_review_pr_state.work_item_id`
 * and a missing pointer (predecessor case) hides the PR sections instead of
 * erroring — never the current PR's state on an old case.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import type {
  CodeReviewPrReviewsResponse,
  CodeReviewReport,
  WorkItem,
  WorkItemGate,
} from "@yagni/shared";
import { Chip } from "../../ui/index.js";
import { StatusPill } from "../../ui/status-pill.js";
import { Markdown } from "../../components/Markdown.js";
import { useWorkItemActivity } from "../../hooks/useWorkItemActivity.js";
import { useAuthSession } from "../../hooks/useWorkspaceRole.js";
import { resolveShowsRunCost } from "../../hooks/useShowsRunCost.js";
import { useOpenWorkItemGate } from "../../tracker/useWorkItemGate.js";
import { formatRelative } from "../../lib/relativeTime.js";
import { workItemStatus } from "../workLifecycle.js";
import { accentStyle, workItemPill } from "../workAtoms.js";
import { originLine } from "../recordAtoms.js";
import { useTeamIdentity } from "../useTeamIdentity.js";
import { StageSpine } from "../StageSpine.js";
import { Journey } from "../Journey.js";
import { WorkerChip } from "../WorkerChip.js";
import { useReviewFindings } from "../ReviewFindings.js";
import { GateBlock } from "../GateBlock.js";
import { HistoryList } from "../HistoryBlock.js";
import {
  deriveReceipts,
  findLinkedIssue,
  Receipts,
  SourceBlock,
  RunsBlock,
} from "../caseBlocks.js";
import {
  formatMillicents,
  RUN_TONE,
  runIsWaitingOnHuman,
  useLiveItem,
  useWorkItemRuns,
} from "../useWorkItemRuns.js";
import { useRunLiveFlip } from "../useRunLiveFlip.js";
import { deliveredFindingsOf } from "../usePullRequestReview.js";
import { StageTracker } from "../StageTracker.js";
import { CorrectionsBlock } from "../CorrectionsBlock.js";
import type { WorkerLookup } from "../useWorkerLookup.js";
import {
  RERUN_LABEL,
  RERUN_QUEUED,
  REVIEW_AGAIN_EFFECT,
  REVIEW_AGAIN_LABEL,
  REVIEW_AGAIN_QUEUED,
  REVIEW_AGAIN_QUEUED_MERGED,
  REPORT_STATUS_CHIP,
  REPORT_STATUS_TONE,
  REVIEW_PASS_CHIP,
  REVIEW_PREVIEW_INLINE_TITLE,
  REVIEW_PREVIEW_TITLE,
  GITHUB_EVENT_CHIP,
  GITHUB_EVENT_TONE,
  VERDICT_CALL_CHIP,
  VERDICT_CALL_EXPLAINER,
  VERDICT_CALL_EXPLAINER_UPGRADED,
  VERDICT_CALL_TONE,
  durationLabel,
  refutedCountLine,
  runHistoryLine,
  summaryFindingsLine,
} from "../../workers/copy.js";
import { Finding } from "../../ui/index.js";
import { CaseSection, CaseSectionTitle } from "./CaseSection.js";
import { SpecialistsRoster, specialistsSummary } from "./SpecialistsSection.js";
import { stripMarkerLines } from "./pipeline.js";
import { useReviewCasePr } from "./useReviewCase.js";
import { reviewFailureSentence, reviewRetryLine } from "./reviewCopy.js";
import "../work.css";
import "../caseFile.css";
import "../../decisions/decisions.css";
import "./reviewCase.css";

export interface ReviewCaseFileProps {
  item: WorkItem;
  deskName: string;
  accent: string;
  /** Resolve a worker slug (WorkShell's one directory read). */
  workerOf?: WorkerLookup;
  /** Advance after resolving: the Work pager on the page, next review in the dialog. */
  onNext: () => void;
  /** Where this body is mounted; only the exit verbs differ. */
  context?: "page" | "dialog";
}

/** The PR facts, from the live read when present, the sourceRef otherwise. */
function prFacts(
  item: WorkItem,
  prData: CodeReviewPrReviewsResponse | null,
): { prNumber: number | null; repoFullName: string | null; prUrl: string | null } {
  if (prData) {
    return {
      prNumber: prData.pr.prNumber,
      repoFullName: prData.pr.repoFullName,
      prUrl: prData.pr.prUrl,
    };
  }
  const ref = (item.sourceRef ?? {}) as Record<string, unknown>;
  const prNumber = typeof ref.prNumber === "number" ? ref.prNumber : null;
  const repoFullName = typeof ref.repoFullName === "string" ? ref.repoFullName : null;
  return {
    prNumber,
    repoFullName,
    prUrl:
      prNumber != null && repoFullName
        ? `https://github.com/${repoFullName}/pull/${prNumber}`
        : null,
  };
}

/**
 * THE REVIEW card: the newest run's own facts, verdict chip first. The
 * composed preview renders only when no gate is open — while a draft is held,
 * the findings cards below ARE the content, and saying it twice would bury
 * the decisions under a copy of themselves.
 *
 * Exported so the BUILD item's case file can print the same card under its
 * own "proctor's review" section (`CaseFile.tsx`). One review, one rendering:
 * the two surfaces must not grow two vocabularies for one verdict.
 */
export function ReviewCard({
  report,
  followUp,
  hasOpenGate,
  autonomyWord,
  findingsShownBelow = false,
  hideLabel = false,
  retry = null,
}: {
  report: CodeReviewReport;
  followUp: boolean;
  hasOpenGate: boolean;
  /** The run's autonomy rung ("training") — THE RUN card's one unique fact. */
  autonomyWord?: string | null;
  /**
   * The surface above this card already named it. The build item's case file
   * prints "proctor's review" as its section label and then mounted a card
   * that said "the review" directly underneath — one object, two headings, and
   * the second one less specific than the first. The review case file names
   * nothing above the card, so it keeps the label.
   */
  hideLabel?: boolean;
  /**
   * Something below this card is already printing the findings, so the
   * composed preview would be the same review said twice — the same reason a
   * held draft suppresses it. The build item's case file passes this; nothing
   * in the review case file does, so its rendering is unchanged.
   */
  findingsShownBelow?: boolean;
  /**
   * Whether Proctor tries again by itself (the PR-state read). Printed only
   * under a failed report: the caller passes it for the newest report alone.
   */
  retry?: CodeReviewPrReviewsResponse["retry"];
}) {
  const duration = durationLabel(report.startedAt, report.completedAt);
  const refutedCount =
    report.refinementDecisions?.verifications?.filter(
      (v) => v.verdict === "refuted" && v.failed !== true,
    ).length ?? 0;
  // Counted as they posted (a refuted finding never posts, an unverified one
  // posts as a nit), so the card and the pull request name the same findings.
  const baseFindingsLine = report.refinementDecisions
    ? summaryFindingsLine(deliveredFindingsOf(report))
    : null;
  const findingsLine =
    baseFindingsLine && refutedCount > 0
      ? `${baseFindingsLine} · ${refutedCountLine(refutedCount)}`
      : baseFindingsLine;
  const preview = !hasOpenGate && !findingsShownBelow ? report.reviewPreview : null;
  const retryLine = report.status === "failed" ? reviewRetryLine(retry) : null;

  return (
    <div className="rvc-review-card" data-testid="rvc-review-card">
      <div className="rvc-review-card-lab">
        {hideLabel ? null : <span>the review</span>}
        {report.event ? (
          // What GitHub received is the fact the pull request shows; the
          // model's own call is a detail in the facts row below.
          <Chip tone={GITHUB_EVENT_TONE[report.event]} data-testid="rvc-github-event">
            {GITHUB_EVENT_CHIP[report.event]}
          </Chip>
        ) : report.verdict ? (
          // The verdict is the fact; what "their call" means is a sentence
          // explaining it, so it rides the chip as a hover title (the one-line
          // rule the dock already follows) rather than printing beside it —
          // and as an aria-label carrying both, because a title alone reaches
          // a mouse and nobody else.
          <Chip
            tone={VERDICT_CALL_TONE[report.verdict]}
            data-testid="rvc-verdict-call"
            title={
              report.verdictUpgraded ? VERDICT_CALL_EXPLAINER_UPGRADED : VERDICT_CALL_EXPLAINER
            }
            aria-label={`${VERDICT_CALL_CHIP[report.verdict]} · ${
              report.verdictUpgraded ? VERDICT_CALL_EXPLAINER_UPGRADED : VERDICT_CALL_EXPLAINER
            }`}
          >
            {VERDICT_CALL_CHIP[report.verdict]}
          </Chip>
        ) : (
          <Chip tone={REPORT_STATUS_TONE[report.status]}>{REPORT_STATUS_CHIP[report.status]}</Chip>
        )}
      </div>
      <div className="rvc-review-facts">
        {autonomyWord ? (
          <>
            <span data-testid="rvc-autonomy-word">{autonomyWord}</span>
            <span aria-hidden>·</span>
          </>
        ) : null}
        <span>{followUp ? REVIEW_PASS_CHIP.followUp : REVIEW_PASS_CHIP.initial}</span>
        {report.event && report.verdict ? (
          <>
            <span aria-hidden>·</span>
            <span
              data-testid="rvc-verdict-call"
              title={
                report.verdictUpgraded ? VERDICT_CALL_EXPLAINER_UPGRADED : VERDICT_CALL_EXPLAINER
              }
            >
              {VERDICT_CALL_CHIP[report.verdict]}
            </span>
          </>
        ) : null}
        {report.verdict ? (
          <>
            <span aria-hidden>·</span>
            <span>{REPORT_STATUS_CHIP[report.status]}</span>
          </>
        ) : null}
        {duration ? (
          <>
            <span aria-hidden>·</span>
            <span>{duration}</span>
          </>
        ) : null}
        {report.headSha ? (
          <>
            <span aria-hidden>·</span>
            <span>head {report.headSha.slice(0, 7)}</span>
          </>
        ) : null}
      </div>
      {findingsLine ? (
        <p className="rvc-review-findings-line" data-testid="rvc-findings-line">
          {findingsLine}
        </p>
      ) : null}
      {report.failure ? (
        <p className="rvc-review-failure" data-testid="rvc-review-failure">
          {reviewFailureSentence(report.failure.reason)}
        </p>
      ) : null}
      {report.failure && retryLine ? (
        <p className="rvc-review-failure" data-testid="rvc-review-retry">
          {retryLine}
        </p>
      ) : null}
      {preview ? (
        <div className="rvc-preview" data-testid="rvc-preview">
          <p className="rvc-preview-title">{REVIEW_PREVIEW_TITLE}</p>
          <Markdown text={stripMarkerLines(preview.body)} />
          {preview.comments.length > 0 ? (
            <>
              <p className="rvc-preview-title">{REVIEW_PREVIEW_INLINE_TITLE}</p>
              <ul className="wrk-review-findings">
                {preview.comments.map((comment, index) => (
                  <Finding
                    key={index}
                    file={comment.file}
                    line={comment.line}
                    body={comment.body}
                  />
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function ReviewCaseFile({
  item,
  deskName,
  accent,
  workerOf,
  onNext,
  context = "page",
}: ReviewCaseFileProps) {
  // The ticket sheet renders this outside the routed outlet, so the session
  // comes from useAuthSession, which reads the shell's when the outlet has none.
  const session = useAuthSession();
  const viewerId = session?.user?.id ?? null;
  const { gate, isLoading: gateLoading, refetch: refetchGate } = useOpenWorkItemGate(item.id);
  const {
    runs,
    totalSellMillicents,
    loading: runsLoading,
    reload: reloadRuns,
  } = useWorkItemRuns(item.id);
  const { activity, loading: activityLoading } = useWorkItemActivity(item.id);
  const review = useReviewFindings(item.id, gate?.kind === "review_result");
  const pr = useReviewCasePr(item.id);
  const worker = workerOf?.(item.personaSlug) ?? null;
  const { enrichmentOf } = useTeamIdentity();
  const [rerunNote, setRerunNote] = useState<string | null>(null);
  const terminal = item.phase === "done" || item.phase === "dismissed";

  // The journey reads every gate the item ever carried. Settled items only.
  const [gateHistory, setGateHistory] = useState<WorkItemGate[]>([]);
  useEffect(() => {
    if (!terminal) {
      setGateHistory([]);
      return;
    }
    let cancelled = false;
    fetch(`/api/work-items/${encodeURIComponent(item.id)}/gates`, { credentials: "include" })
      .then((r) => (r.ok ? (r.json() as Promise<{ gates?: WorkItemGate[] }>) : null))
      .then((body) => {
        if (!cancelled) setGateHistory(Array.isArray(body?.gates) ? body.gates : []);
      })
      .catch(() => {
        if (!cancelled) setGateHistory([]);
      });
    return () => {
      cancelled = true;
    };
  }, [item.id, terminal]);

  // Live flip: run events reflow the gate, the roll-up, and the PR read.
  const onLiveFlip = useCallback(() => {
    reloadRuns();
    void refetchGate();
    void pr.refetch();
  }, [reloadRuns, refetchGate, pr]);
  useRunLiveFlip(item.id, onLiveFlip);

  const handleResolved = useCallback(() => {
    // The same broadcast the generic case file fires — reflows the Work list
    // wherever it is open. The pane stays on this item and re-renders into
    // its settled state; moving on stays the reader's call (pager / close).
    window.dispatchEvent(new CustomEvent("tickets.invalidate"));
    void refetchGate();
    reloadRuns();
    void pr.refetch();
  }, [refetchGate, reloadRuns, pr]);

  const linkedIssue = findLinkedIssue(item);
  // The header reads the item as the runs roll-up proves it. A review that
  // finished while this pane held the item it loaded mid-review kept saying
  // "Reviewing now" over a roll-up that said complete (staging, NEARM-397).
  const liveItem = useLiveItem(item, runs, runsLoading);
  const status = workItemStatus(liveItem);
  const pill = workItemPill(liveItem);
  const origin = originLine(item);
  const facts = prFacts(item, pr.data);

  const runActive = runs.some((r) => RUN_TONE[r.status] === "running");
  const currentRun = runs[0] ?? null;
  const runWaiting = !terminal && currentRun != null && runIsWaitingOnHuman(currentRun);
  const viewerIsResolver =
    item.assigneeType === "human" && item.assigneeId != null && item.assigneeId === viewerId;
  const centerWorking = status.tone === "working" || runActive;
  const headerPill = runWaiting
    ? viewerIsResolver
      ? { label: "Waiting on you", tone: "needsYou" as const }
      : { label: "Waiting on approval", tone: "yourGate" as const }
    : centerWorking
      ? { label: "Reviewing", tone: "executing" as const }
      : pill;

  const newestReport = pr.data?.reviews[0] ?? null;
  const olderReports = pr.data ? pr.data.reviews.slice(1) : [];
  const receipts = useMemo(() => deriveReceipts(item, runs), [item, runs]);

  // Null on the pull-request plan: the summary drops "$x total".
  const totalLine = resolveShowsRunCost(session) ? formatMillicents(totalSellMillicents) : null;
  const moreDetailsSummary = [
    receipts.length > 0 ? `${receipts.length} receipt${receipts.length === 1 ? "" : "s"}` : null,
    runs.length > 0 ? `${runs.length} run${runs.length === 1 ? "" : "s"}` : null,
    totalLine ? `${totalLine} total` : null,
  ]
    .filter((part): part is string => Boolean(part))
    .join(" · ");

  const onRerun = () => {
    void pr.rerun().then(({ ok, message }) => {
      setRerunNote(ok ? RERUN_QUEUED : message);
    });
  };
  // A merged or closed pull request is off the watch, so the re-arm refuses
  // it (NEARM-396). The Team's own review request is the path that works,
  // and it needs the Team: a case with none has nowhere to ask from.
  const prSettled = pr.data?.pr.githubStatus === "merged" || pr.data?.pr.githubStatus === "closed";
  const reviewAgainDeskId = prSettled ? item.deskId : null;
  const onReviewAgain = () => {
    if (!reviewAgainDeskId) return;
    const merged = pr.data?.pr.githubStatus === "merged";
    void pr.requestAgain(reviewAgainDeskId).then(({ ok, message }) => {
      setRerunNote(ok ? (merged ? REVIEW_AGAIN_QUEUED_MERGED : REVIEW_AGAIN_QUEUED) : message);
    });
  };

  const exitLabel = context === "dialog" ? "Close" : "Back to Work";

  return (
    <div
      className="tri-read wk-read cf-read rvc-read"
      style={accentStyle(accent)}
      data-testid="review-case-file"
    >
      {/* ── header: the spine leads; the title row and byline carry the rest
             (the same anatomy as the commissioned case file) ── */}
      <StageSpine item={item} />
      <div className="cf-head-row">
        <h1 className="cf-title">
          {item.title}
          {facts.prNumber != null && facts.prUrl ? (
            <a
              className="rvc-title-pr"
              href={facts.prUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="rvc-title-pr"
            >
              #{facts.prNumber}
            </a>
          ) : null}
        </h1>
        <span className="cf-head-pill">
          <StatusPill tone={headerPill.tone} pulse={headerPill.tone === "needsYou"}>
            {headerPill.label}
          </StatusPill>
        </span>
      </div>
      <div className="cf-byline">
        <span className="cf-byline-team">
          <span className="cf-byline-dot" style={{ background: accent }} aria-hidden />
          {deskName}
        </span>
        <WorkerChip worker={worker} testId="rvc-worker" />
        <span className="cf-byline-fact">
          {item.identifier ? <b>{item.identifier}</b> : null}
          {item.identifier ? " · " : ""}opened {formatRelative(item.createdAt)}
          {origin ? ` · ${origin}` : ""}
          {facts.repoFullName && facts.prNumber != null && facts.prUrl ? (
            <>
              {" · "}
              <a href={facts.prUrl} target="_blank" rel="noreferrer">
                {facts.repoFullName}#{facts.prNumber}
              </a>
            </>
          ) : null}
          {pr.data && !prSettled ? (
            <>
              {" · "}
              <button
                type="button"
                className="rvc-rerun-inline"
                onClick={onRerun}
                data-testid="rvc-rerun"
              >
                {RERUN_LABEL}
              </button>
            </>
          ) : null}
          {pr.data && reviewAgainDeskId ? (
            <>
              {" · "}
              <button
                type="button"
                className="rvc-rerun-inline"
                title={REVIEW_AGAIN_EFFECT}
                onClick={onReviewAgain}
                data-testid="rvc-review-again"
              >
                {REVIEW_AGAIN_LABEL}
              </button>
            </>
          ) : null}
          {rerunNote ? (
            <>
              {" · "}
              <span className="rvc-rerun-note" data-testid="rvc-rerun-note">
                {rerunNote}
              </span>
            </>
          ) : null}
        </span>
      </div>

      {/* ── THE ASK / the settled receipt ── */}
      {terminal ? (
        <>
          {!item.archivedAt && (
            <div className="wk-gate cf-settled" data-testid="rvc-outcome">
              <div className={"is-receipt" + (item.phase === "done" ? "" : " held")}>
                <span className="is-receipt-ic" aria-hidden>
                  <Check width={16} height={16} />
                </span>
                <div className="is-receipt-body">
                  <div className="is-receipt-lab">{status.label}</div>
                  <p className="is-receipt-t">
                    {item.phase === "done" ? "Complete and on the record." : "No action was taken."}
                  </p>
                </div>
              </div>
              <div className="tri-actions">
                <RouterLink
                  className="is-quiet cf-receipt-link"
                  to={`/work/${encodeURIComponent(item.id)}/receipt`}
                >
                  The receipt <ArrowRight width={13} height={13} aria-hidden />
                </RouterLink>
                <button type="button" className="is-quiet" onClick={onNext}>
                  {exitLabel}
                </button>
              </div>
            </div>
          )}
          {newestReport ? (
            <ReviewCard
              report={newestReport}
              followUp={pr.data?.pr.followUp ?? false}
              hasOpenGate={false}
              retry={pr.data?.retry ?? null}
            />
          ) : null}
          {item.description && (
            <div className="wk-art">
              <Markdown className="wk-art-note" text={item.description} />
            </div>
          )}
          <Journey item={item} activity={activity} gates={gateHistory} workerOf={workerOf} />
        </>
      ) : (
        <>
          <CaseSectionTitle title="The ask" />
          {newestReport ? (
            <ReviewCard
              report={newestReport}
              followUp={pr.data?.pr.followUp ?? false}
              hasOpenGate={gate != null}
              retry={pr.data?.retry ?? null}
            />
          ) : pr.isLoading && !pr.prMissing ? (
            <p className="wk-gate-copy">
              <Loader2 width={14} height={14} className="wk-spin" aria-hidden /> Reading the review
              record…
            </p>
          ) : null}

          {item.archivedAt ? (
            // Archived: honest state. The way back is the page header's
            // Unarchive (the dialog is a review reader, not a filing surface).
            <div className="wk-gate">
              <div className="wk-gate-lab">
                Archived {formatRelative(item.archivedAt)}. It's out of every list until you bring
                it back.
              </div>
              <div className="tri-actions">
                <button type="button" className="is-quiet" onClick={onNext}>
                  {exitLabel}
                </button>
              </div>
            </div>
          ) : gateLoading ? (
            <div className="wk-gate wk-gate-loading">
              <Loader2 width={16} height={16} className="wk-spin" aria-hidden /> Loading the gate…
            </div>
          ) : gate ? (
            <GateBlock
              gate={gate}
              review={review}
              workerName={worker?.displayName ?? null}
              resolverName={session?.user?.displayName ?? null}
              onResolved={handleResolved}
            />
          ) : (
            <div className="wk-gate">
              <div className={"wk-gate-lab" + (centerWorking ? " live" : "")}>
                {centerWorking && <span className="wk-badge-pulse" aria-hidden />}
                {centerWorking ? "Reviewing now" : "No action is waiting on you here right now."}
              </div>
              <div className="tri-actions">
                <button type="button" className="is-quiet" onClick={onNext}>
                  {exitLabel}
                </button>
              </div>
            </div>
          )}

          {/* No RunSurface here: review runs journal no plan, commits, or log,
              so the card only ever showed fallback copy. Its one unique fact
              (the autonomy rung) rides THE REVIEW card's facts line. */}
          {runActive && <StageTracker workItemId={item.id} />}
        </>
      )}

      {/* ── SPECIALISTS ── */}
      {newestReport ? (
        <CaseSection
          title="Specialists"
          summary={specialistsSummary(newestReport)}
          collapsible={false}
          testId="rvc-specialists"
        >
          <SpecialistsRoster report={newestReport} />
        </CaseSection>
      ) : null}

      {/* ── MORE DETAILS ── */}
      <CaseSection title="More details" summary={moreDetailsSummary || null} testId="rvc-more">
        <div className="cf-more-body">
          <SourceBlock item={item} teamName={deskName} />

          {receipts.length > 0 && (
            <>
              <div className="cf-sect">The Receipts</div>
              <Receipts receipts={receipts} />
            </>
          )}

          <CorrectionsBlock workItemId={item.id} />

          {/* Rows link to the Run detail page — the in-page work-record fold
              is retired (2026-08-30). */}
          <RunsBlock
            runs={runs}
            loading={runsLoading}
            workItemId={item.id}
            totalSellMillicents={totalSellMillicents}
          />

          {olderReports.length > 0 ? (
            <>
              {/* Older passes only: the newest run's facts are THE REVIEW
                  card's job — this list is what came before. */}
              <div className="cf-sect">Review passes</div>
              <ul className="rvc-run-history" data-testid="rvc-run-history">
                {olderReports.map((run) => (
                  <li key={run.id}>{runHistoryLine(run)}</li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      </CaseSection>

      {/* ── HISTORY ── */}
      {!activityLoading && activity.length > 0 ? (
        <CaseSection title="History" summary={String(activity.length)} testId="rvc-history">
          <HistoryList activity={activity} />
        </CaseSection>
      ) : null}
    </div>
  );
}
